/* Zoom control for the Print previews: −, the level (relative to "fit", click = fit), +, Fit. The viewports pan by
 * dragging or scrolling once zoomed in; Ctrl/⌘ + wheel also zooms. Shared by the single-image preview and the nest. */
import { useTranslation } from 'react-i18next';

export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 8;
export const ZOOM_STEP = 1.25;
export const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

export function ZoomBar({ zoom, onZoom, dark }) {
  const { t } = useTranslation('bv');
  const pct = Math.round(zoom * 100);
  return (
    <div className={`bv-zoombar${dark ? ' bv-zoombar--dark' : ''}`} role="group" aria-label={t('print.zoom.aria')} data-testid="zoom-bar">
      <button type="button" onClick={() => onZoom(clampZoom(zoom / ZOOM_STEP))} disabled={zoom <= ZOOM_MIN + 1e-6} aria-label={t('print.zoom.out')} title={t('print.zoom.out')}>−</button>
      <button type="button" className="bv-zoombar__pct" onClick={() => onZoom(1)} title={t('print.zoom.fitTitle')} data-testid="zoom-pct">{pct}%</button>
      <button type="button" onClick={() => onZoom(clampZoom(zoom * ZOOM_STEP))} disabled={zoom >= ZOOM_MAX - 1e-6} aria-label={t('print.zoom.in')} title={t('print.zoom.in')}>+</button>
      <button type="button" className="bv-zoombar__fit" onClick={() => onZoom(1)} disabled={Math.abs(zoom - 1) < 1e-6} data-testid="zoom-fit">{t('print.zoom.fit')}</button>
    </div>
  );
}

/** Ctrl/⌘ + wheel → zoom (non-passive listener so the page does not zoom); plain wheel keeps scrolling the viewport. */
export function bindWheelZoom(el, getZoom, setZoom) {
  if (!el) return () => {};
  const onWheel = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    setZoom(clampZoom(getZoom() * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
  };
  el.addEventListener('wheel', onWheel, { passive: false });
  return () => el.removeEventListener('wheel', onWheel);
}

/** Drag the viewport's background to pan when the content is bigger than the viewport (scrolling container). */
export function bindDragPan(el) {
  if (!el) return () => {};
  let drag = null;
  const down = (e) => {
    if (e.button !== 0 || e.target.closest('button, a, input, select, textarea')) return;
    if (el.scrollWidth <= el.clientWidth && el.scrollHeight <= el.clientHeight) return;
    drag = { x: e.clientX, y: e.clientY, l: el.scrollLeft, t: el.scrollTop };
    el.classList.add('is-panning');
  };
  const move = (e) => { if (!drag) return; el.scrollLeft = drag.l - (e.clientX - drag.x); el.scrollTop = drag.t - (e.clientY - drag.y); };
  const up = () => { drag = null; el.classList.remove('is-panning'); };
  el.addEventListener('pointerdown', down);
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  return () => { el.removeEventListener('pointerdown', down); window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
}
